import { StringParam, useQueryParams, withDefault } from "use-query-params";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import React, { useEffect, useState } from "react";
import cn from "classnames";
import { useTranslation } from "../../hooks/useTranslation";
import { InputSelect, SelectOption } from "../../components/InputSelect";
import { InputDateTimeMoscow } from "../../components/InputDateTimeMoscow";
import { useFilePicker } from "use-file-picker";
import { faUpload } from "@fortawesome/free-solid-svg-icons/faUpload";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { twMerge } from "tailwind-merge";

type Props = {
  isLoading: boolean;
  className?: string;
  onApply: (params: RightsIntersectionsFiltersParams) => Promise<any>;
};

export type RightsIntersectionsFiltersParams = {
  search_type?: string;
  date_from?: string;
  date_to?: string;
  release_ids?: string;
  file?: any;
  limit?: number;
  offset?: number;
};

export enum IntersectionSearchType {
  FILE = "file",
  RELEASE_ID = "releaseIds",
}

function SearchTypeOptions(t: any): SelectOption[] {
  return [
    {
      label: `${t("rights_intersections.file")}`,
      value: IntersectionSearchType.FILE
    },
    {
      label: `${t("rights_intersections.release_id")}`,
      value: IntersectionSearchType.RELEASE_ID
    }
  ];
}

export const RightsIntersectionsFilters: React.FC<Props> = ({ isLoading, className, onApply }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<Blob | null>(null);
  const [query, setQuery] = useQueryParams({
    search_type: withDefault(StringParam, IntersectionSearchType.FILE),
    date_from: StringParam,
    date_to: StringParam,
    release_id: StringParam
  });

  const [open, { plainFiles }] = useFilePicker({
    accept: [".csv"],
    multiple: false,
    onFilesSuccessfulySelected: async (data) => {
      const selectedFile = data.plainFiles[0];
      setSelectedFile(selectedFile);
    }
  });

  useEffect(() => {
    if (query.search_type !== IntersectionSearchType.FILE) {
      void onApply({ ...query, file: selectedFile } as RightsIntersectionsFiltersParams);
    }
  }, []);

  useEffect(() => {
    document.onkeyup = function(e) {
      if (e.key === "Enter" && (e.target as any)?.tagName !== "TEXTAREA") {
        void onApply({ ...query, file: selectedFile } as RightsIntersectionsFiltersParams);
      }
    };

    return () => {
      document.onkeyup = null;
    };
  }, [query, selectedFile, onApply]);


  return (
    <div className={twMerge(cn({ "mb-3": query.search_type === IntersectionSearchType.RELEASE_ID }, className))}>
      <div className={cn("flex gap-2")}>
        <InputSelect
          value={query.search_type}
          containerClassName={"min-w-[150px]"}
          options={SearchTypeOptions(t)}
          disabled={isLoading}
          onChange={(v) => {
            setQuery({ search_type: v.value as IntersectionSearchType });
          }}
        />

        <InputDateTimeMoscow
          withTime={false}
          className={"px-2 py-1.5"}
          disabled={isLoading}
          value={query.date_from}
          onChange={(v) => {
            setQuery({ date_from: v != null && v?.trim().length > 0 ? v : null });
          }} />

        <InputDateTimeMoscow
          withTime={false}
          disabled={isLoading}
          value={query.date_to}
          className={"px-2 py-1.5"}
          onChange={(v) => {
            setQuery({ date_to: v != null && v?.trim().length > 0 ? v : null });
          }} />

        {
          query.search_type === IntersectionSearchType.FILE &&
          <Button
            disabled={isLoading}
            icon={faUpload}
            className={cn(plainFiles?.length > 0 ? "bg-green-500 text-white hover:bg-green-400" : "", "w-[201px]")}
            label={plainFiles?.length > 0 ? "Файл выбран" : "Выбрать CSV-файл"}
            onClick={open}
          />
        }

        {
          query.search_type === IntersectionSearchType.RELEASE_ID &&
          <Input
            disabled={isLoading}
            style={{ width: "250px" }}
            defaultValue={query.release_id ?? ""}
            placeholder={`${t("rights_intersections.release_id")}`}
            onChange={(v) =>
              setQuery({ release_id: v.trim().length > 0 ? v : null })
            }
          />
        }

        <Button
          disabled={
            query.search_type === IntersectionSearchType.FILE && plainFiles?.length === 0
            || query.search_type === IntersectionSearchType.RELEASE_ID && query.release_id == null
            || isLoading
          }
          icon={query.search_type === IntersectionSearchType.FILE ? faDownload : faSearch}
          label={query.search_type === IntersectionSearchType.FILE ? "Загрузить отчет" : `${t("search")}`}
          className={"bg-blue-500 text-white hover:bg-blue-400"}
          onClick={() => onApply({ ...query, file: selectedFile } as RightsIntersectionsFiltersParams)}
        />
      </div>
    </div>
  );
};
